import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { emailWhitelistService, EmailWhitelistItem } from '@/services/emailWhitelistService';
import { toastUtils } from '@/common/utils/toastUtils';
import { useNavigationStore } from '@/common/store';
import { 
  Shield, 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  Mail, 
  X, 
  Check,
  AlertTriangle,
  Loader2
} from 'lucide-react';

/**
 * 이메일 화이트리스트 관리 페이지 (개발용)
 * 라우트: /dev/sys/internal/wl-mgmt-x7k9
 */
const EmailWhitelistManagement: React.FC = () => {
  const {   setHeaderTitle } = useNavigationStore();
  const [whitelist, setWhitelist] = useState<EmailWhitelistItem[]>([]);
  const [filteredList, setFilteredList] = useState<EmailWhitelistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingItem, setEditingItem] = useState<EmailWhitelistItem | null>(null);
  const [formEmail, setFormEmail] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 삭제 확인 모달
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    
    setHeaderTitle('Email Whitelist Management (Dev Only)');
    fetchWhitelist();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = whitelist.filter(
        item => 
          item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredList(filtered);
    } else {
      setFilteredList(whitelist);
    }
  }, [searchQuery, whitelist]);

  const fetchWhitelist = async () => {
    setIsLoading(true);
    try {
      const data = await emailWhitelistService.getWhitelist();
      setWhitelist(data);
      setFilteredList(data);
    } catch (error) {
      toastUtils.error('화이트리스트를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormEmail('');
    setFormDescription('');
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item: EmailWhitelistItem) => {
    setModalMode('edit');
    setFormEmail(item.email);
    setFormDescription(item.description || '');
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormEmail('');
    setFormDescription('');
    setEditingItem(null);
  };

  const handleSubmit = async () => {
    if (!formEmail.trim()) {
      toastUtils.error('이메일을 입력해주세요.');
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formEmail)) {
      toastUtils.error('올바른 이메일 형식이 아닙니다.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (modalMode === 'add') {
        await emailWhitelistService.addEmail(formEmail, formDescription);
        toastUtils.success('이메일이 추가되었습니다.');
      } else if (editingItem) {
        await emailWhitelistService.updateEmail(editingItem.id, formEmail, formDescription);
        toastUtils.success('이메일이 수정되었습니다.');
      }
      closeModal();
      fetchWhitelist();
    } catch (error: any) {
      toastUtils.error(error.response?.data?.message || '처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(true);
    try {
      await emailWhitelistService.deleteEmail(id);
      toastUtils.success('이메일이 삭제되었습니다.');
      setDeleteConfirmId(null);
      fetchWhitelist();
    } catch (error: any) {
      toastUtils.error(error.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container>
      <ContentWrapper>
        {/* 헤더 */}
        <Header>
          <HeaderIconWrapper>
            <Shield size={28} />
          </HeaderIconWrapper>
          <HeaderContent>
            <HeaderTitle>Email Whitelist Management</HeaderTitle>
            <HeaderDescription>
              개발 환경 전용 이메일 화이트리스트 관리 페이지입니다.
            </HeaderDescription>
          </HeaderContent>
          <WarningBadge>
            <AlertTriangle size={14} />
            DEV ONLY
          </WarningBadge>
        </Header>

        {/* 액션 바 */}
        <ActionBar>
          <SearchWrapper>
            <SearchIcon>
              <Search size={18} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="이메일 또는 설명으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <ClearButton onClick={() => setSearchQuery('')}>
                <X size={16} />
              </ClearButton>
            )}
          </SearchWrapper>
          <AddButton onClick={openAddModal}>
            <Plus size={18} />
            이메일 추가
          </AddButton>
        </ActionBar>

        {/* 통계 */}
        <StatsBar>
          <StatItem>
            <StatLabel>전체</StatLabel>
            <StatValue>{whitelist.length}</StatValue>
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatLabel>검색 결과</StatLabel>
            <StatValue>{filteredList.length}</StatValue>
          </StatItem>
        </StatsBar>

        {/* 목록 */}
        <ListContainer>
          {isLoading ? (
            <LoadingWrapper>
              <LoadingSpinner>
                <Loader2 size={32} />
              </LoadingSpinner>
              <LoadingText>화이트리스트를 불러오는 중...</LoadingText>
            </LoadingWrapper>
          ) : filteredList.length === 0 ? (
            <EmptyWrapper>
              <EmptyIcon>
                <Mail size={48} />
              </EmptyIcon>
              <EmptyText>
                {searchQuery 
                  ? '검색 결과가 없습니다.' 
                  : '등록된 화이트리스트가 없습니다.'}
              </EmptyText>
              {!searchQuery && (
                <EmptyButton onClick={openAddModal}>
                  <Plus size={16} />
                  첫 번째 이메일 추가하기
                </EmptyButton>
              )}
            </EmptyWrapper>
          ) : (
            <ListTable>
              <TableHeader>
                <TableHeaderCell $width="40%">이메일</TableHeaderCell>
                <TableHeaderCell $width="25%">설명</TableHeaderCell>                
                <TableHeaderCell $width="15%">액션</TableHeaderCell>
              </TableHeader>
              <TableBody>
                {filteredList.map((item, index) => (
                  <TableRow key={item.id} $delay={index * 0.03}>
                    <TableCell>
                      <EmailWrapper>
                        <EmailIcon>
                          <Mail size={16} />
                        </EmailIcon>
                        <EmailText>{item.email}</EmailText>
                      </EmailWrapper>
                    </TableCell>
                    <TableCell>
                      <DescriptionText>
                        {item.description || '-'}
                      </DescriptionText>
                    </TableCell>                    
                    <TableCell>
                      <ActionButtons>
                        <ActionButton 
                          $type="edit" 
                          onClick={() => openEditModal(item)}
                          title="수정"
                        >
                          <Pencil size={14} />
                        </ActionButton>
                        <ActionButton 
                          $type="delete" 
                          onClick={() => setDeleteConfirmId(item.id)}
                          title="삭제"
                        >
                          <Trash2 size={14} />
                        </ActionButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ListTable>
          )}
        </ListContainer>
      </ContentWrapper>

      {/* 추가/수정 모달 */}
      {isModalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {modalMode === 'add' ? '이메일 추가' : '이메일 수정'}
              </ModalTitle>
              <ModalCloseButton onClick={closeModal}>
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>이메일 *</FormLabel>
                <FormInput
                  type="email"
                  placeholder="example@email.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>이름 *</FormLabel>
                <FormInput
                  type="text"
                  placeholder="이름을 입력해주세요"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <ModalButton $variant="cancel" onClick={closeModal}>
                취소
              </ModalButton>
              <ModalButton 
                $variant="confirm" 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    처리 중...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    {modalMode === 'add' ? '추가' : '수정'}
                  </>
                )}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirmId !== null && (
        <ModalOverlay onClick={() => setDeleteConfirmId(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} $small>
            <ModalHeader>
              <ModalTitle>삭제 확인</ModalTitle>
              <ModalCloseButton onClick={() => setDeleteConfirmId(null)}>
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DeleteWarning>
                <AlertTriangle size={24} />
                <DeleteWarningText>
                  정말 이 이메일을 화이트리스트에서 삭제하시겠습니까?
                  <br />
                  <strong>이 작업은 되돌릴 수 없습니다.</strong>
                </DeleteWarningText>
              </DeleteWarning>
            </ModalBody>
            <ModalFooter>
              <ModalButton $variant="cancel" onClick={() => setDeleteConfirmId(null)}>
                취소
              </ModalButton>
              <ModalButton 
                $variant="danger" 
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    삭제 중...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    삭제
                  </>
                )}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: ${semanticColors.background.primary};
  padding: 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 16px;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const HeaderIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  color: white;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.h1`
  ${typography.heading.h3}
  color: white;
  margin: 0 0 4px 0;
`;

const HeaderDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

const WarningBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 100px;
  color: white;
  ${typography.caption.caption1.medium}
  font-weight: 700;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  animation: ${fadeIn} 0.4s ease-out 0.1s both;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 640px) {
    max-width: 100%;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${semanticColors.text.tertiary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 44px;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  
  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${semanticColors.border.brand};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${semanticColors.text.secondary};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: none;
  border-radius: 12px;
  color: white;
  ${typography.paragraph.p2.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 20px;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  margin-bottom: 16px;
  animation: ${fadeIn} 0.4s ease-out 0.15s both;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const StatValue = styled.span`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  font-weight: 700;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${semanticColors.border.primary};
`;

const ListContainer = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out 0.2s both;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  color: ${semanticColors.text.tertiary};
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
`;

const EmptyIcon = styled.div`
  color: ${semanticColors.text.tertiary};
  opacity: 0.5;
`;

const EmptyText = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.tertiary};
`;

const EmptyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    color: ${semanticColors.text.primary};
  }
`;

const ListTable = styled.div`
  width: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  padding: 16px 20px;
  background: ${semanticColors.background.tertiary};
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const TableHeaderCell = styled.div<{ $width: string }>`
  width: ${props => props.$width};
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
  font-weight: 600;
`;

const TableBody = styled.div``;

const TableRow = styled.div<{ $delay: number }>`
  display: flex;
  padding: 16px 20px;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
  transition: background 0.2s ease;
  animation: ${fadeIn} 0.3s ease-out ${props => props.$delay}s both;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  
  &:nth-child(1) { width: 40%; }
  &:nth-child(2) { width: 25%; }
  &:nth-child(3) { width: 20%; }
  &:nth-child(4) { width: 15%; }
`;

const EmailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const EmailIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(220, 38, 38, 0.1);
  border-radius: 8px;
  color: #dc2626;
`;

const EmailText = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const DescriptionText = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
`;

const DateText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $type: 'edit' | 'delete' }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$type === 'edit' 
    ? 'rgba(59, 130, 246, 0.1)' 
    : 'rgba(220, 38, 38, 0.1)'};
  border: none;
  border-radius: 8px;
  color: ${props => props.$type === 'edit' ? '#3b82f6' : '#dc2626'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$type === 'edit' 
      ? 'rgba(59, 130, 246, 0.2)' 
      : 'rgba(220, 38, 38, 0.2)'};
    transform: scale(1.1);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div<{ $small?: boolean }>`
  background: ${semanticColors.background.primary};
  border-radius: 20px;
  width: ${props => props.$small ? '400px' : '480px'};
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const ModalTitle = styled.h3`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const FormInput = styled.input`
  padding: 12px 16px;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  
  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${semanticColors.border.brand};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const ModalButton = styled.button<{ $variant: 'cancel' | 'confirm' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  ${typography.paragraph.p2.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'cancel':
        return `
          background: ${semanticColors.background.secondary};
          border: 1px solid ${semanticColors.border.primary};
          color: ${semanticColors.text.secondary};
          
          &:hover {
            background: ${semanticColors.background.primaryHover};
          }
        `;
      case 'confirm':
        return `
          background: linear-gradient(135deg, #059669 0%, #10b981 100%);
          border: none;
          color: white;
          
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border: none;
          color: white;
          
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const DeleteWarning = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
  color: #dc2626;
`;

const DeleteWarningText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  text-align: center;
  margin: 0;
  line-height: 1.6;
  
  strong {
    color: #dc2626;
  }
`;

export default EmailWhitelistManagement;

